// lib/logger.ts
// Structured JSON logger — outputs machine-readable logs for server-side code.
// In development, logs are pretty-printed for readability. In production, each
// log is a single JSON line for ingestion by log aggregators (Datadog, Loki).
//
// Usage:
//   import { logger, createRequestLogger } from '@/lib/logger'
//
//   logger.info('Server started', { port: 3000 })
//   logger.error('Failed to fetch', err, { url: '/api/projects' })
//
//   // Request-scoped logger (carries requestId through all logs):
//   const log = createRequestLogger('req-abc-123')
//   log.info('Processing request', { path: '/api/projects' })

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

/** Structured log entry written to stdout/stderr. */
interface LogEntry {
  timestamp: string
  level: LogLevel
  message: string
  service: string
  requestId?: string
  userId?: string
  projectId?: string
  module?: string
  /** Structured contextual data attached to the log */
  context?: Record<string, unknown>
  /** Structured error details (only present at error level) */
  error?: {
    name: string
    message: string
    stack?: string
    code?: string
  }
}

/** Priority values for log level filtering. */
const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
}

/**
 * Resolve minimum log level from environment.
 * Defaults to 'debug' in development, 'info' in production.
 * Override with LOG_LEVEL env var.
 */
function getMinLevel(): LogLevel {
  const envLevel = import.meta.env.VITE_LOG_LEVEL as LogLevel | undefined
  if (envLevel && envLevel in LOG_LEVEL_PRIORITY) return envLevel
  return import.meta.env.MODE === 'production' ? 'info' : 'debug'
}

/** Check if a given level should be emitted based on the minimum level. */
function shouldLog(level: LogLevel): boolean {
  return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[getMinLevel()]
}

/**
 * Format an unknown error value into a structured error object.
 * Returns undefined if the input is falsy.
 */
function formatError(err: unknown): LogEntry['error'] | undefined {
  if (!err) return undefined

  if (err instanceof Error) {
    return {
      name: err.name,
      message: err.message,
      stack: import.meta.env.MODE !== 'production' ? err.stack : undefined,
      code:
        'code' in err
          ? String((err as Record<string, unknown>).code)
          : undefined,
    }
  }

  return { name: 'UnknownError', message: String(err) }
}

/**
 * Emit a log entry to the appropriate console method.
 * In development: pretty-printed JSON for readability.
 * In production: single-line JSON for log aggregator ingestion.
 */
function emit(entry: LogEntry): void {
  // Strip undefined fields for cleaner output
  const clean = JSON.parse(JSON.stringify(entry)) as LogEntry

  const output =
    import.meta.env.MODE !== 'production'
      ? JSON.stringify(clean, null, 2)
      : JSON.stringify(clean)

  switch (entry.level) {
    case 'error':
      console.error(output)
      break
    case 'warn':
      console.warn(output)
      break
    case 'debug':
      console.debug(output)
      break
    default:
      console.log(output)
  }
}

/** Context carried by all loggers. */
interface LoggerContext {
  service?: string
  requestId?: string
  userId?: string
  projectId?: string
  module?: string
}

/** Logger interface returned by createLogger / createRequestLogger. */
export interface Logger {
  debug: (message: string, data?: Record<string, unknown>) => void
  info: (message: string, data?: Record<string, unknown>) => void
  warn: (message: string, data?: Record<string, unknown>) => void
  error: (
    message: string,
    err?: unknown,
    data?: Record<string, unknown>,
  ) => void
  /** Create a child logger with additional context fields. */
  child: (childContext: Partial<LoggerContext>) => Logger
}

/**
 * Internal log function shared by all logger instances.
 */
function log(
  ctx: LoggerContext,
  level: LogLevel,
  message: string,
  data?: Record<string, unknown>,
  err?: unknown,
): void {
  if (!shouldLog(level)) return

  const entry: LogEntry = {
    timestamp: new Date().toISOString(),
    level,
    message,
    service: ctx.service ?? 'web',
    requestId: ctx.requestId,
    userId: ctx.userId,
    projectId: ctx.projectId,
    module: ctx.module,
  }

  if (data && Object.keys(data).length > 0) entry.context = data

  const formattedError = formatError(err)
  if (formattedError) entry.error = formattedError

  emit(entry)
}

/**
 * Create a logger instance with the given context.
 * All log entries produced by this logger include the context fields
 * (service, requestId, userId, projectId, module).
 *
 * @example
 *   const log = createLogger({ module: 'auth' })
 *   log.info('User logged in', { email: 'user@example.com' })
 */
export function createLogger(ctx: LoggerContext = {}): Logger {
  const loggerInstance: Logger = {
    debug: (message, data) => log(ctx, 'debug', message, data),
    info: (message, data) => log(ctx, 'info', message, data),
    warn: (message, data) => log(ctx, 'warn', message, data),
    error: (message, err, data) => log(ctx, 'error', message, data, err),
    child: (childContext) => createLogger({ ...ctx, ...childContext }),
  }
  return loggerInstance
}

/**
 * Create a request-scoped logger.
 * Convenience wrapper around createLogger that sets the requestId.
 *
 * @param requestId - The X-Request-Id header value or a generated UUID
 * @param extra - Optional additional context (userId, projectId, module)
 *
 * @example
 *   const log = createRequestLogger('req-abc-123')
 *   log.info('Handling GET /api/projects')
 *   log.error('Database query failed', err, { query: 'SELECT ...' })
 */
export function createRequestLogger(
  requestId: string,
  extra?: Omit<LoggerContext, 'requestId'>,
): Logger {
  return createLogger({
    service: 'web',
    requestId,
    ...extra,
  })
}

/**
 * Default logger singleton — used when you don't have request context.
 * Prefer createRequestLogger() inside API route handlers.
 *
 * @example
 *   import { logger } from '@/lib/logger'
 *   logger.info('Application started')
 */
export const logger = createLogger({ service: 'web' })
